import { Box, LinearProgress } from '@mui/material';
import { formatNumberWithPrecision } from '../../../utils/number';
import { REPORT_CHART_COLORS } from './chart-theme';

const TRACK_WIDTH = 96;
const TRACK_HEIGHT = 8;

type RetentionBarCellProps = {
  value: number;
  minScale: number;
  maxScale: number;
  anchorValue?: number;
  warningThreshold?: number;
  dangerThreshold?: number;
};

function resolveSimpleBarColor(
  value: number,
  warningThreshold: number | undefined,
  dangerThreshold: number | undefined,
): string {
  if (dangerThreshold !== undefined && value < dangerThreshold) {
    return REPORT_CHART_COLORS.churn;
  }
  if (warningThreshold !== undefined && value < warningThreshold) {
    return REPORT_CHART_COLORS.contraction;
  }
  return REPORT_CHART_COLORS.endingMrr;
}

export const RetentionBarCell: React.FC<RetentionBarCellProps> = ({
  value,
  minScale,
  maxScale,
  anchorValue,
  warningThreshold,
  dangerThreshold,
}) => {
  const valueText = (
    <Box component="span" sx={{ minWidth: 56, textAlign: 'right' }}>
      {formatNumberWithPrecision(value, 0)}%
    </Box>
  );

  if (anchorValue === undefined) {
    const progress = Math.min(
      Math.max(((value - minScale) / (maxScale - minScale)) * 100, 0),
      100,
    );
    const barColor = resolveSimpleBarColor(value, warningThreshold, dangerThreshold);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          justifyContent: "center"
        }}>
        {valueText}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={(theme) => ({
            width: TRACK_WIDTH,
            height: TRACK_HEIGHT,
            borderRadius: `${TRACK_HEIGHT / 2}px`,
            backgroundColor: theme.palette.grey[200],
            '& .MuiLinearProgress-bar': {
              borderRadius: `${TRACK_HEIGHT / 2}px`,
              backgroundColor: barColor,
            },
          })}
        />
      </Box>
    );
  }

  const trackRange = maxScale - minScale || 1;
  const clampedValue = Math.min(Math.max(value, minScale), maxScale);
  const clampedAnchor = Math.min(Math.max(anchorValue, minScale), maxScale);
  const valuePct = ((clampedValue - minScale) / trackRange) * 100;
  const anchorPct = ((clampedAnchor - minScale) / trackRange) * 100;
  const blueWidth = Math.min(valuePct, anchorPct);
  const greenWidth = valuePct > anchorPct ? valuePct - anchorPct : 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        justifyContent: "center"
      }}>
      {valueText}
      <Box
        sx={[{
          position: "relative"
        }, (theme) => ({
          width: TRACK_WIDTH,
          height: TRACK_HEIGHT,
          backgroundColor: theme.palette.grey[200],
          borderRadius: `${TRACK_HEIGHT / 2}px`,
          overflow: 'hidden',
        })]}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${blueWidth}%`,
            backgroundColor: REPORT_CHART_COLORS.endingMrr
          }} />
        {greenWidth > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              height: "100%",
              left: `${anchorPct}%`,
              width: `${greenWidth}%`,
              backgroundColor: REPORT_CHART_COLORS.expansion
            }} />
        )}
        <Box
          sx={[{
            position: "absolute",
            top: 0,
            bottom: 0
          }, (theme) => ({
            left: `${anchorPct}%`,
            width: '1px',
            backgroundColor: theme.palette.grey[500],
          })]} />
      </Box>
    </Box>
  );
};
